import { createSlice } from '@reduxjs/toolkit';
import { themes, resolveActiveTheme } from '../../utils/themes';

const getInitialTheme = () => {
  if (typeof window !== 'undefined') {
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme;
      }
    } catch (e) {
      console.warn('LocalStorage access denied', e);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const initialState = {
  theme: getInitialTheme(), // light or dark
  activeTheme: resolveActiveTheme(), // dynamically resolved via URL preview, schedule, or default
  sidebarOpen: false,
  notifications: [],
  loading: false,
  themeSettings: null,
};

// Helper: Apply CSS variables & semantic design tokens to root
export const applyThemeColors = (themeKey) => {
  const resolvedKey = (themeKey || 'default').toLowerCase();
  const theme = themes[resolvedKey] || themes['default'];
  if (!themes[resolvedKey]) console.warn(`Theme '${resolvedKey}' not found, falling back to default`);

  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // 1. Apply Tailwind RGB color channels (--primary-50 to --primary-900, --secondary-*, --accent-*)
  if (theme.colors) {
    Object.entries(theme.colors).forEach(([variable, value]) => {
      root.style.setProperty(variable, value);
    });
  }

  // 2. Apply Semantic Design Tokens (--color-primary, --color-surface, --color-border, --color-glow, etc.)
  if (theme.tokens) {
    Object.entries(theme.tokens).forEach(([token, value]) => {
      root.style.setProperty(token, value);
    });
  }

  // 3. Set data-theme attribute for CSS selectors
  root.setAttribute('data-theme', resolvedKey);
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('theme', state.theme);
        } catch (e) {
          console.warn('LocalStorage access denied', e);
        }
        document.documentElement.classList.toggle('dark', state.theme === 'dark');
      }
    },
    setTheme: (state, action) => {
      state.theme = action.payload;
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('theme', state.theme);
        } catch (e) {
          console.warn('LocalStorage access denied', e);
        }
        document.documentElement.classList.toggle('dark', state.theme === 'dark');
      }
    },
    setActiveTheme: (state, action) => {
      state.activeTheme = action.payload;
    },
    setThemeSettings: (state, action) => {
      state.themeSettings = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    closeSidebar: (state) => {
      state.sidebarOpen = false;
    },
    addNotification: (state, action) => {
      // Prevent duplicate notifications with same message
      const existingNotification = state.notifications.find(
        notification => notification.message === action.payload.message
      );

      if (!existingNotification) {
        state.notifications.push({
          id: Date.now(),
          ...action.payload,
        });
      }
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
});

export const {
  toggleTheme,
  setTheme,
  setActiveTheme,
  setThemeSettings,
  toggleSidebar,
  closeSidebar,
  addNotification,
  removeNotification,
  clearNotifications,
  setLoading,
} = uiSlice.actions;

export default uiSlice.reducer;